import {
  FacebookAuthProvider,
  GoogleAuthProvider,
  linkWithPopup,
  TwitterAuthProvider,
  getRedirectResult,
  linkWithCredential,
  signInWithPopup,
  signInWithRedirect,
  type User,
  type UserCredential,
} from "firebase/auth";
import { FirebaseError } from "firebase/app";
import { firebaseAuth, googleProvider, facebookProvider, twitterProvider } from "@/lib/firebase";
import { shouldUseMobileExperience } from "@/utils/device";

export type SocialProvider = "google" | "facebook" | "twitter";

export interface FirebaseLoginUser {
  uid: string;
  email: string;
  displayName: string;
  photoURL: string;
  provider: string;
  idToken: string;
}

export async function buildFirebaseLoginUserFromUser(user: User, provider: SocialProvider): Promise<FirebaseLoginUser> {
  const token = await user.getIdToken();

  return {
    uid: user.uid,
    email: user.email ?? "",
    displayName: user.displayName ?? user.email?.split("@")[0] ?? `${provider} user`,
    photoURL: user.photoURL ?? "",
    provider,
    idToken: token,
  };
}

export interface SerializedPendingCredential {
  provider: SocialProvider;
  accessToken?: string;
  idToken?: string;
  secret?: string;
}

const firebaseRedirectProviderKey = "cinema:firebase-redirect-provider";
let firebaseRedirectLoginPromise: Promise<FirebaseLoginUser | null> | null = null;

export async function signInWithProvider(
  providerName: SocialProvider,
  providerInstance: typeof googleProvider | typeof facebookProvider | typeof twitterProvider
): Promise<FirebaseLoginUser | null> {
  if (shouldUseMobileExperience()) {
    window.sessionStorage.setItem(firebaseRedirectProviderKey, providerName);
    await signInWithRedirect(firebaseAuth, providerInstance);
    return null;
  }

  const credential: UserCredential = await signInWithPopup(firebaseAuth, providerInstance);
  const { user } = credential;
  const token = await user.getIdToken();

  return {
    uid: user.uid,
    email: user.email ?? "",
    displayName: user.displayName ?? user.email?.split("@")[0] ?? `${providerName} user`,
    photoURL: user.photoURL ?? "",
    provider: providerName,
    idToken: token,
  };
}

export async function signInWithGoogle(): Promise<FirebaseLoginUser | null> {
  return signInWithProvider("google", googleProvider);
}

export async function signInWithFacebook(): Promise<FirebaseLoginUser | null> {
  return signInWithProvider("facebook", facebookProvider);
}

export async function signInWithTwitter(): Promise<FirebaseLoginUser | null> {
  return signInWithProvider("twitter", twitterProvider);
}

export async function signInWithSocialProvider(provider: SocialProvider): Promise<FirebaseLoginUser | null> {
  switch (provider) {
    case "google":
      return signInWithGoogle();
    case "facebook":
      return signInWithFacebook();
    case "twitter":
    default:
      return signInWithTwitter();
  }
}

export function hasPendingFirebaseRedirect(): boolean {
  if (typeof window === "undefined") return false;
  return Boolean(window.sessionStorage.getItem(firebaseRedirectProviderKey));
}

async function consumeFirebaseRedirectLoginOnce(): Promise<FirebaseLoginUser | null> {
  const pendingProvider = window.sessionStorage.getItem(firebaseRedirectProviderKey) as SocialProvider | null;
  try {
    const credential = await getRedirectResult(firebaseAuth);
    if (!credential) return null;

    const providerId = credential.providerId || credential.user.providerData[0]?.providerId || "";
    const provider: SocialProvider = pendingProvider
      || (providerId === "google.com" ? "google" : providerId === "facebook.com" ? "facebook" : "twitter");
    return buildFirebaseLoginUserFromUser(credential.user, provider);
  } finally {
    window.sessionStorage.removeItem(firebaseRedirectProviderKey);
  }
}

export function consumeFirebaseRedirectLogin(): Promise<FirebaseLoginUser | null> {
  if (!firebaseRedirectLoginPromise) {
    firebaseRedirectLoginPromise = consumeFirebaseRedirectLoginOnce();
  }
  return firebaseRedirectLoginPromise;
}

function getProviderInstance(provider: SocialProvider) {
  switch (provider) {
    case "google":
      return googleProvider;
    case "facebook":
      return facebookProvider;
    case "twitter":
    default:
      return twitterProvider;
  }
}

export async function linkCurrentUserWithProvider(provider: SocialProvider): Promise<FirebaseLoginUser> {
  const currentUser = firebaseAuth.currentUser;
  if (!currentUser) {
    throw new Error("No signed-in Firebase user available for popup linking");
  }

  await linkWithPopup(currentUser, getProviderInstance(provider));
  await currentUser.reload();
  const refreshedUser = firebaseAuth.currentUser ?? currentUser;
  return buildFirebaseLoginUserFromUser(refreshedUser, provider);
}

export function getPendingCredentialFromError(error: FirebaseError): SerializedPendingCredential | null {
  const facebookCredential = FacebookAuthProvider.credentialFromError(error) as unknown as Record<string, unknown> | null;
  if (facebookCredential && typeof facebookCredential.accessToken === "string") {
    return {
      provider: "facebook",
      accessToken: facebookCredential.accessToken,
    };
  }

  const googleCredential = GoogleAuthProvider.credentialFromError(error) as unknown as Record<string, unknown> | null;
  if (
    googleCredential &&
    (typeof googleCredential.idToken === "string" || typeof googleCredential.accessToken === "string")
  ) {
    return {
      provider: "google",
      idToken: typeof googleCredential.idToken === "string" ? googleCredential.idToken : undefined,
      accessToken: typeof googleCredential.accessToken === "string" ? googleCredential.accessToken : undefined,
    };
  }

  const twitterCredential = TwitterAuthProvider.credentialFromError(error) as unknown as Record<string, unknown> | null;
  if (twitterCredential && typeof twitterCredential.accessToken === "string" && typeof twitterCredential.secret === "string") {
    return {
      provider: "twitter",
      accessToken: twitterCredential.accessToken,
      secret: twitterCredential.secret,
    };
  }

  return null;
}

export async function linkPendingCredential(pendingCredential: SerializedPendingCredential): Promise<void> {
  const currentUser = firebaseAuth.currentUser;
  if (!currentUser) {
    throw new Error("No signed-in Firebase user available for linking");
  }

  switch (pendingCredential.provider) {
    case "facebook":
      if (!pendingCredential.accessToken) {
        throw new Error("Missing Facebook access token for linking");
      }
      await linkWithCredential(currentUser, FacebookAuthProvider.credential(pendingCredential.accessToken));
      return;
    case "google":
      if (!pendingCredential.idToken && !pendingCredential.accessToken) {
        throw new Error("Missing Google credential for linking");
      }
      await linkWithCredential(
        currentUser,
        GoogleAuthProvider.credential(pendingCredential.idToken ?? null, pendingCredential.accessToken ?? null)
      );
      return;
    case "twitter":
      if (!pendingCredential.accessToken || !pendingCredential.secret) {
        throw new Error("Missing X credential for linking");
      }
      await linkWithCredential(currentUser, TwitterAuthProvider.credential(pendingCredential.accessToken, pendingCredential.secret));
      return;
    default:
      throw new Error("Unsupported pending credential provider");
  }
}
